import { get, isEmpty } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { DOMAIN_SIGN_IN } from "utils/api/graphql/mutations/users";
import { LINK } from "utils/api/graphql/queries/link";
import { PROJECT } from "utils/api/graphql/queries/projects";
import { TRACER } from "utils/api/graphql/queries/tracers";
import { KEYS, SCREENS } from "utils/constants";

const isValidParam = (value) => value && value !== "undefined";

const Init = () => {
  const navigate = useNavigate();
  const [signIn] = useMutation(DOMAIN_SIGN_IN, { fetchPolicy: "no-cache" });
  const { src } = useParams();
  const [host, setHost] = useState(null);
  const [getTracer] = useLazyQuery(TRACER);
  const [getProject] = useLazyQuery(PROJECT);
  const [getLink] = useLazyQuery(LINK);

  const { queryParams, queryString, url } = useMemo(() => {
    const url = window.location.href;
    const hash = window.location.hash;
    let normalizedHash = hash;
    if (hash && (hash.match(/\?/g) || []).length > 1) {
      const parts = hash.split("?");
      normalizedHash = parts.shift() + "?" + parts.join("&");
    }
    const queryParams = new URLSearchParams(normalizedHash?.split("?")[1]);
    const queryString = hash.includes("?")
      ? "?" + normalizedHash.split("?")[1]
      : "";
    return { queryParams, queryString, url };
  }, []);

  const navigateToProject = useCallback(
    (tracer, projectId, subscriptionId, statusId) => {
      const process = get(omitDeep(tracer, "__typename"), "flow.process");
      navigate(
        `${SCREENS.PRIVATE.GUEST.PROJECTS.DETAIL.path}/${projectId}${queryString}`,
        {
          state: {
            process,
            tracer: omitDeep(tracer, "__typename"),
            subscriptionId: isValidParam(subscriptionId)
              ? subscriptionId
              : undefined,
            status: get(process, "processStatuses", []).find(
              ({ id }) => id === statusId,
            ),
          },
        },
      );
    },
    [navigate, queryString],
  );

  const navigateToCreate = useCallback(() => {
    localStorage.setItem(KEYS.TEMPLATE_HISTORY, "{}");
    navigate(`${SCREENS.PUBLIC.PROCESSES.CREATE.path}/${src}${queryString}`);
  }, [navigate, src, queryString]);

  const handleProjectNavigation = useCallback(() => {
    const projectId = localStorage.getItem(KEYS.PROJECT_ID);
    const subscriptionId = localStorage.getItem(KEYS.SUBSCRIPTION_ID);
    const statusId = localStorage.getItem(KEYS.STATUS_ID);

    localStorage.removeItem(KEYS.USER);

    if (isValidParam(projectId)) {
      getProject({
        fetchPolicy: "no-cache",
        variables: { where: { id: projectId } },
        onCompleted: ({ project }) => {
          if (isEmpty(project)) {
            return navigateToCreate();
          }
          getTracer({
            variables: { where: { source: { id: src } } },
            onCompleted: ({ tracer }) => {
              navigateToProject(tracer, projectId, subscriptionId, statusId);
            },
          });
        },
      });
    } else {
      navigateToCreate();
    }
  }, [src, getTracer, navigateToProject, navigateToCreate]);

  useEffect(() => {
    const currentSource = localStorage.getItem(KEYS.SOURCE);
    if (currentSource && currentSource !== src) {
      localStorage.clear();
    }
    localStorage.setItem(KEYS.SOURCE, src);

    if (window.self === window.top) {
      const projectIdParams = queryParams.get("p");
      const cuidParams = queryParams.get("cuid");
      if (isValidParam(projectIdParams)) {
        localStorage.setItem(KEYS.PROJECT_ID, projectIdParams);
      }

      const [cleanedBaseUrl] = url.split("?");
      if (cuidParams) return setHost(`${cleanedBaseUrl}?cuid=${cuidParams}`);
      setHost(cleanedBaseUrl);
      return;
    }

    const handleMessage = (event) => {
      if (
        !process.env.REACT_APP_PROCESS_FLOWS.split(",").includes(event.origin)
      ) {
        return;
      }

      const { url: parentUrl } = event.data;
      const parsedUrl = new URL(parentUrl);
      const params = new URLSearchParams(parsedUrl.search);

      const paramMap = {
        p: KEYS.PROJECT_ID,
        l: KEYS.LINK_ID,
        s: KEYS.SUBSCRIPTION_ID,
        status: KEYS.STATUS_ID,
      };

      Object.entries(paramMap).forEach(([key, storageKey]) => {
        const value = params.get(key);
        if (isValidParam(value)) {
          localStorage.setItem(storageKey, value);
        }
      });

      setHost(parsedUrl.host);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [src, url, queryParams]);

  useEffect(() => {
    if (!host) return;

    signIn({
      variables: { data: { name: host } },
      onCompleted: ({ domainSignIn }) => {
        localStorage.setItem(KEYS.ACCESS_TOKEN, domainSignIn?.accessToken);

        const linkIdParams = queryParams.get("l");
        const link = linkIdParams || localStorage.getItem(KEYS.LINK_ID);

        if (isValidParam(link)) {
          getLink({
            fetchPolicy: "no-cache",
            variables: { where: { id: link } },
            onCompleted: ({ link }) => {
              const projectId = get(link, "project.id");
              const subscriptionId = get(link, "subscription.id");
              const statusId = get(link, "fields.status.id");

              if (projectId) localStorage.setItem(KEYS.PROJECT_ID, projectId);
              if (subscriptionId)
                localStorage.setItem(KEYS.SUBSCRIPTION_ID, subscriptionId);
              if (statusId) localStorage.setItem(KEYS.STATUS_ID, statusId);

              handleProjectNavigation();
            },
          });
          return;
        }

        handleProjectNavigation();
      },
    });
  }, [host, signIn, queryParams, getLink, handleProjectNavigation]);
};

export default Init;
